import React, { useContext, useEffect, useState } from 'react';
import { EventProps } from './Event.types';
import * as Styles from '../../directory/DirectoryService/DirectoryService.styles';
import Row from '../../components/Row/Row';
import Column from '../../components/Column/Column';
import Heading from '../../components/Heading/Heading';
import ServiceContact from '../../directory/ServiceContact/ServiceContact';
import SummaryList from '../../components/SummaryList/SummaryList';
import { transformSnippet } from '../../directory/DirectoryService/DirectoryServiceTransform';
import DirectoryMap from '../../directory/DirectoryMap/DirectoryMap';
import DirectoryAddToShortList from '../../directory/DirectoryAddToShortList/DirectoryAddToShortList';
import DownloadableFiles from '../../slices/DownloadableFiles/DownloadableFiles';
import HeartIcon from '../../components/icons/HeartIcon/HeartIcon';
import { ThemeContext } from 'styled-components';
import { useDirectoryShortListContext } from '../../contexts/DirectoryShortListProvider/DirectoryShortListProvider';
import Button from '../../components/Button/Button';
import ServiceSocialLinks from '../../directory/ServiceSocialLinks/ServiceSocialLinks';

const Event: React.FunctionComponent<EventProps> = ({
  id,
  name,
  description,
  descriptionElement,
  startDateTime,
  endDateTime,
  eventType,
  audience,
  organization,
  location,
  email,
  fees,
  url,
  websiteUrlTitle,
  contacts,
  uploads,
  socialProfiles,
  shortListPath,
  isRecurring,
  recurrencePattern,
  registrationDeadline,
  capacity,
  currentAttendees,
}) => {
  const labelLetters: string[] = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L'];
  const [notServer, setNotServer] = useState<boolean>(false);
  const themeContext = useContext(ThemeContext);
  const {
    favourites: { favourites: favourites },
  } = useDirectoryShortListContext();

  useEffect(() => {
    setNotServer(true);
  }, []);

  const formatEventDateTime = (startDateTime: string, endDateTime?: string) => {
    const startDate = new Date(startDateTime);
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    };

    let formatted = startDate.toLocaleDateString('en-GB', options);

    if (endDateTime) {
      const endDate = new Date(endDateTime);
      const endTime = endDate.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
      formatted += ` - ${endTime}`;
    }

    return formatted;
  };

  const transformEventDetails = () => {
    const details = [];

    if (fees) {
      details.push({
        term: 'Cost',
        definition: fees,
      });
    }

    if (eventType) {
      details.push({
        term: 'Event type',
        definition: eventType,
      });
    }

    if (audience && audience.length > 0) {
      details.push({
        term: 'Target audience',
        definition: audience.join(', '),
      });
    }

    if (isRecurring && recurrencePattern) {
      details.push({
        term: 'Recurrence',
        definition: recurrencePattern,
      });
    }

    if (registrationDeadline) {
      const deadline = new Date(registrationDeadline);
      details.push({
        term: 'Registration deadline',
        definition: deadline.toLocaleDateString('en-GB', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        }),
      });
    }

    if (capacity && currentAttendees !== undefined) {
      details.push({
        term: 'Availability',
        definition: `${currentAttendees} / ${capacity} registered`,
      });
    } else if (capacity) {
      details.push({
        term: 'Capacity',
        definition: `${capacity} spaces`,
      });
    }

    return details;
  };

  return (
    <Styles.Container data-testid="Event">
      <Row>
        <Column small="full" medium="full" large="full">
          <Row>
            <Column small="full" medium="full" large="full">
              <Styles.ServiceHeader>
                <Heading level={1} text={name} />
                {organization?.logo && (
                  <Styles.ServiceImageOuter>
                    <Styles.ServiceImageContainer>
                      <Styles.ServiceImage src={organization.logo} alt={name + ' logo'} loading="lazy" />
                    </Styles.ServiceImageContainer>
                  </Styles.ServiceImageOuter>
                )}
              </Styles.ServiceHeader>
            </Column>

            <Column small="full" medium="full" large="full">
              {notServer && (
                <Styles.ShortListLinks>
                  {shortListPath && (
                    <Styles.Favourites href={shortListPath}>
                      <HeartIcon colourFill={themeContext.theme_vars.colours.action} /> Shortlist ({favourites.length})
                    </Styles.Favourites>
                  )}
                  <DirectoryAddToShortList
                    id={id}
                    name={name}
                    snippet={transformSnippet(description, 350)}
                    email={email}
                    website={url}
                    phone={contacts?.[0]?.phones?.flatMap((phone) => phone.number).join(', ')}
                    addresses={location?.address ? [{
                      id: location.address.id,
                      address_1: location.address.address1,
                      city: location.address.city,
                      state_province: location.address.county,
                      postal_code: location.address.postcode,
                      country: location.address.country
                    }] : []}
                    fees={fees}
                  />
                </Styles.ShortListLinks>
              )}
            </Column>
          </Row>
        </Column>

        {/* Event Date and Time */}
        <Column small="full" medium="full" large="full" classes="striped-column">
          <Heading level={2} text="Event date and time" />
          <div style={{ fontSize: '1.2rem', fontWeight: 'bold', marginBottom: '1rem' }}>
            {formatEventDateTime(startDateTime, endDateTime)}
          </div>
          {organization && (
            <p>
              <strong>Organised by:</strong> {organization.name}
            </p>
          )}
        </Column>

        {/* Location */}
        {location && (location.address || location.isVirtual) && (
          <Column small="full" medium="full" large="full" classes="striped-column">
            <Row>
              <Column small="full" medium="full" large="one-third">
                {location.isVirtual ? (
                  <div>
                    <Heading level={2} text="Virtual event" />
                    <p>This is an online event.</p>
                    {location.virtualMeetingInfo && (
                      <div>
                        <strong>Meeting details:</strong>
                        <div dangerouslySetInnerHTML={{ __html: location.virtualMeetingInfo }} />
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <Heading level={2} text={location.name || 'Event location'} />
                    {location.address && (
                      <Styles.PhysicalAddress>
                        <p
                          dangerouslySetInnerHTML={{
                            __html: Object.values(location.address)
                              .filter((item) => item !== '' && item !== location.address?.id)
                              .join(' <br />'),
                          }}
                        />
                        {location.latitude && location.longitude && (
                          <Button
                            url={`https://google.com/maps/dir//${location.latitude},${location.longitude}`}
                            text="Get directions"
                          />
                        )}
                      </Styles.PhysicalAddress>
                    )}
                    {location.accessibility && location.accessibility.length > 0 && (
                      <>
                        <Heading level={3} text="Accessibility" />
                        <ul>
                          {location.accessibility.map((accessibility) => (
                            <li key={accessibility.id}>{accessibility.accessibility}</li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                )}
              </Column>

              <Column small="full" medium="full" large="two-thirds">
                {!location.isVirtual &&
                  location.latitude &&
                  location.longitude && (
                    <>
                      {notServer && (
                        <DirectoryMap
                          mapProps={{
                            centre: `${location.latitude},${location.longitude}`,
                            imageAltText: `${location.name || 'Event location'} shown on a map`,
                            zoom: 14,
                            size: '640x320',
                            mapMarkers: [
                              {
                                lat: parseFloat(location.latitude),
                                lng: parseFloat(location.longitude),
                                label: labelLetters[0],
                                title: location.name || 'Event location',
                              },
                            ],
                          }}
                        />
                      )}
                      <Styles.MapLink
                        href={`https://www.google.com/maps/search/?api=1&query=${location.latitude}%2C${location.longitude}`}
                      >
                        View in Google Maps
                      </Styles.MapLink>
                    </>
                  )}
              </Column>
            </Row>
          </Column>
        )}

        {/* Contact */}
        {(email || url || contacts?.length > 0) && (
          <Column small="full" medium="full" large="full" classes="striped-column">
            <Heading level={2} text="How to contact about this event" />
            <ServiceContact email={email} website={url} websiteTitle={websiteUrlTitle} contacts={contacts} />
          </Column>
        )}

        {/* Description */}
        <Column small="full" medium="full" large="full" classes="striped-column">
          <Heading level={2} text="About this event" />
          <div>
            <>{descriptionElement}</>
          </div>
          <SummaryList terms={transformEventDetails()} hasMargin={false} />
        </Column>

        {/* Files/Resources */}
        {uploads?.length > 0 && (
          <Column small="full" medium="full" large="full" classes="striped-column">
            <Heading level={2} text="Event resources" />
            <DownloadableFiles files={uploads} />
          </Column>
        )}

        {/* Social Media */}
        {socialProfiles?.length > 0 && (
          <Column small="full" medium="full" large="full" classes="striped-column">
            <Heading level={2} text="Follow this event" />
            <ServiceSocialLinks profiles={socialProfiles} />
          </Column>
        )}
      </Row>
    </Styles.Container>
  );
};

export default Event;